'use client';

// useSuggestedQuestions - Consumer hook for fetching suggested questions
// Story 4.10: Suggested Questions for New Conversations
//
// Uses plain React state (no TanStack Query) — consistent with consumer app patterns
// (useChat also uses useState/useEffect, not TanStack Query)

import { useState, useEffect, useRef } from 'react';

interface SuggestedQuestionsResponse {
  data: {
    questions: string[];
  };
}

export interface UseSuggestedQuestionsOptions {
  botId: string;
  enabled?: boolean;
}

export interface UseSuggestedQuestionsReturn {
  questions: string[];
  isLoading: boolean;
  error: Error | null;
}

export function useSuggestedQuestions({
  botId,
  enabled = true,
}: UseSuggestedQuestionsOptions): UseSuggestedQuestionsReturn {
  const [questions, setQuestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!enabled || !botId || hasFetched.current) return;
    hasFetched.current = true;

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const response = await fetch(`/api/bots/${botId}/suggested-questions`);
        if (!response.ok) {
          throw new Error('Failed to fetch suggested questions');
        }
        const result: SuggestedQuestionsResponse = await response.json();
        if (!cancelled) {
          setQuestions(result.data.questions);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [botId, enabled]);

  return { questions, isLoading, error };
}

export default useSuggestedQuestions;
